/**
 * SchoolManagement - Standalone page listing all schools/institutions
 * Navigate to /admin/schools
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  GraduationCap, Plus, Users, UserCog, BookOpen, School,
  Loader2, ArrowRight, Building2, Search, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

const SCHOOL_TYPES = [
  { value: 'school', label: 'School' },
  { value: 'college', label: 'College' },
  { value: 'institute', label: 'Institute' },
  { value: 'campus', label: 'Campus' },
  { value: 'university', label: 'University' },
];

const SchoolManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({
    name: '', code: '', type: 'school', address: '', phone: '', email: '', primary_color: '#6366f1'
  });

  const { data: schools = [], isLoading } = useQuery({
    queryKey: ['school-tenants'],
    queryFn: () => api('/tenants'),
  });

  const createMutation = useMutation({
    mutationFn: (data) => api('/tenants', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (result) => {
      toast.success(`${result.name} created successfully`);
      setShowCreate(false);
      setForm({ name: '', code: '', type: 'school', address: '', phone: '', email: '', primary_color: '#6366f1' });
      queryClient.invalidateQueries({ queryKey: ['school-tenants'] });
      navigate(`/admin/schools/${result.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  const filtered = schools.filter(s =>
    !searchQuery || s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || s.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="School Management" showBackButton>
      <div className="space-y-6" data-testid="school-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Schools & Institutions</h1>
            <p className="text-sm text-slate-500 mt-1">Manage all schools in your organization</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-school-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create School
          </Button>
        </div>

        {/* Search */}
        {schools.length > 0 && (
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              className="pl-10"
              placeholder="Search schools..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="search-schools-input"
            />
          </div>
        )}

        {/* Schools Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                <GraduationCap className="h-8 w-8 text-indigo-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">
                {searchQuery ? 'No schools found' : 'No schools yet'}
              </h3>
              <p className="text-sm text-slate-500 mb-4 text-center max-w-md">
                {searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Create your first school to start managing students, teachers, classes and more.'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowCreate(true)} data-testid="create-first-school-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First School
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(school => (
              <Card
                key={school.id}
                className="group cursor-pointer hover:shadow-lg hover:border-indigo-200 transition-all duration-200"
                onClick={() => navigate(`/admin/schools/${school.id}`)}
                data-testid={`school-card-${school.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${school.primary_color || '#6366f1'}15` }}
                    >
                      <GraduationCap className="h-6 w-6" style={{ color: school.primary_color || '#6366f1' }} />
                    </div>
                    <Badge variant="outline" className="capitalize text-xs">
                      {school.type || 'school'}
                    </Badge>
                  </div>

                  <h3 className="font-semibold text-slate-900 text-lg mb-0.5 group-hover:text-indigo-600 transition-colors">
                    {school.name}
                  </h3>
                  <p className="text-xs text-slate-400 font-mono mb-3">{school.code}</p>

                  {school.address && (
                    <p className="text-sm text-slate-500 mb-3 line-clamp-1">{school.address}</p>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {school.email && <span>{school.email}</span>}
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create School Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New School</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2">
              <Label>School Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Faith Academy"
                data-testid="school-name-input"
              />
            </div>
            <div>
              <Label>Code *</Label>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. FWBC"
                maxLength={10}
                data-testid="school-code-input"
              />
              <p className="text-xs text-slate-400 mt-1">Used as prefix for admission numbers</p>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="school-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHOOL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="School address"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+263..."
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="info@school.com"
              />
            </div>
            <div>
              <Label>Brand Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={e => setForm({ ...form, primary_color: e.target.value })}
                  className="h-9 w-12 rounded border cursor-pointer"
                />
                <Input
                  value={form.primary_color}
                  onChange={e => setForm({ ...form, primary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.name || !form.code}
              data-testid="submit-create-school-btn"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create School
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SchoolManagement;
